import { useState, useEffect } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import Plot from "react-plotly.js";
import CorrelationMatrix from "../../testsuite/components/charts/HeatMap";
import { IconLoader2, IconRefresh, IconChevronDown, IconChevronUp } from "@tabler/icons-react";

interface DimensionAnalyticsProps {
    entityCollectionId: string;
}

interface DimensionAnalyticsData {
    dimensions: Array<{
        id: string;
        name: string;
        description: string;
        type: string;
        detailed_type?: string;
        args?: Record<string, any>;
        indexed: boolean;
    }>;
    entity_distributions: Record<string, {
        bins: number[];
        frequencies: number[];
        count: number;
        min: number | null;
        max: number | null;
        mean: number | null;
        std: number | null;
    }>;
    query_distributions: Record<string, {
        bins: number[];
        frequencies: number[];
        count: number;
        min: number | null;
        max: number | null;
        mean: number | null;
        std: number | null;
    }>;
    entity_correlation_matrix: {
        matrix: number[][];
        labels: string[];
    };
    query_correlation_matrix: {
        matrix: number[][];
        labels: string[];
    };
    type_analysis: {
        counts: Record<string, number>;
        percentages: Record<string, number>;
        detailed_counts: Record<string, number>;
        detailed_percentages: Record<string, number>;
        total: number;
    };
    dimension_scores: Record<string, number>;
    weight_analytics: Record<string, {
        average_weight: number | null;
        min_weight: number | null;
        max_weight: number | null;
        std_weight: number | null;
        q1_weight: number | null;
        median_weight: number | null;
        q3_weight: number | null;
        count: number;
    }>;
    score_weight_correlation: number | null;
    score_weight_correlation_pvalue: number | null;
    score_weight_correlation_n: number;
    weight_by_rank_stats: Array<{
        rank: number;
        mean: number;
        min: number;
        q1: number;
        median: number;
        q3: number;
        max: number;
        std: number;
        count: number;
    }>;
    // Information theory metrics
    entity_entropy: Record<string, number | null>;
    query_entropy: Record<string, number | null>;
    entity_mutual_information_matrix: {
        matrix: number[][];
        labels: string[];
    };
    query_mutual_information_matrix: {
        matrix: number[][];
        labels: string[];
    };
    entity_conditional_entropy_matrix: {
        matrix: number[][];
        labels: string[];
    };
    query_conditional_entropy_matrix: {
        matrix: number[][];
        labels: string[];
    };
}

interface IsolationRankSample {
    entity_id: string;
    isolation: number;
    rank: number | null;
    query: string | null;
    note?: string;
}

interface IsolationRankCorrelationData {
    samples: IsolationRankSample[];
    correlation: number | null;
    pvalue: number | null;
    n_samples: number;
    n_total_sampled: number;
    n_with_queries: number;
    bin_percentiles: number[];
    bin_info: Array<{
        bin: number;
        range: [number, number];
        total_in_bin: number;
        sampled: number;
    }>;
    interpretation: string;
    metric: string;
    distance_metric: string;
    summary_stats: {
        mean_isolation: number | null;
        mean_rank: number | null;
        median_rank: number | null;
    };
}

interface NeighbourData {
    entity_id: string;
    content: any;
    distance: number;
}

interface IsolatedEntityData {
    entity_id: string;
    content: any;
    distance_to_closest: number;
}

interface NeighboursResponse {
    neighbours: Record<string, NeighbourData>;
    entity_contents: Record<string, any>;
    most_isolated: IsolatedEntityData[];
}

// Component for expandable entity neighbours
const EntityNeighboursItem = ({ entityId, neighbour, entityContent }: { entityId: string; neighbour: NeighbourData; entityContent: any }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Format entity content for display
    const contentStr = entityContent && typeof entityContent === 'object'
        ? JSON.stringify(entityContent).substring(0, 100)
        : String(entityContent || '').substring(0, 100);
    
    const neighbourContentStr = neighbour.content && typeof neighbour.content === 'object'
        ? JSON.stringify(neighbour.content)
        : String(neighbour.content || '');
    const truncatedNeighbourContent = neighbourContentStr.length > 300 
        ? neighbourContentStr.substring(0, 300) + '...' 
        : neighbourContentStr;
    
    return (
        <div className="bg-gray-900 rounded-lg border border-gray-700">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                        Entity: {entityId.substring(0, 8)}...
                    </div>
                    <div className="text-xs text-gray-400 mt-1 truncate">
                        {contentStr}...
                    </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                    <span className="text-xs text-gray-500">
                        Distance: {neighbour.distance.toFixed(4)}
                    </span>
                    {isExpanded ? <IconChevronUp size={20} className="text-gray-400" /> : <IconChevronDown size={20} className="text-gray-400" />}
                </div>
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-700">
                    <div className="mt-3">
                        <div className="bg-gray-800 rounded p-3 border border-gray-700">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-blue-400 mb-1">
                                        Closest Neighbour: {neighbour.entity_id.substring(0, 8)}...
                                    </div>
                                    <div className="text-xs text-gray-300 break-words">
                                        {truncatedNeighbourContent}
                                    </div>
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                    <span className="text-xs font-medium text-green-400">
                                        {neighbour.distance.toFixed(4)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const DimensionAnalytics = ({ entityCollectionId }: DimensionAnalyticsProps) => {
    const db = useOasisBackend();
    const [analyticsData, setAnalyticsData] = useState<DimensionAnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dimensionFilter, setDimensionFilter] = useState<'live' | 'all'>('live');
    
    // Entity neighbours state
    const [distanceMetric, setDistanceMetric] = useState<"cosine" | "euclidean" | "manhattan">("cosine");
    const [topIsolated, setTopIsolated] = useState<number>(20);
    const [neighboursData, setNeighboursData] = useState<NeighboursResponse | null>(null);
    const [loadingNeighbours, setLoadingNeighbours] = useState(false);
    const [neighboursError, setNeighboursError] = useState<string | null>(null);
    
    // Isolation-Rank correlation state
    const [correlationData, setCorrelationData] = useState<IsolationRankCorrelationData | null>(null);
    const [loadingCorrelation, setLoadingCorrelation] = useState(false);
    const [correlationError, setCorrelationError] = useState<string | null>(null);
    const [correlationSearchType, setCorrelationSearchType] = useState<"oasis" | "semantic">("oasis");
    const [correlationSamplesPerBin, setCorrelationSamplesPerBin] = useState<number>(10);
    const [correlationBins, setCorrelationBins] = useState<number>(5);
    const [correlationDistanceMetric, setCorrelationDistanceMetric] = useState<"cosine" | "euclidean" | "manhattan">("cosine");

    const fetchAnalytics = async () => {
        if (!entityCollectionId) return;
        
        try {
            setLoading(true);
            setError(null);
            // Use axios from the API client instance
            const response = await db.endpoints.instance.get(
                `/api/entity-collection/${entityCollectionId}/dimension-analytics/`,
                {
                    params: {
                        dimension_filter: dimensionFilter
                    }
                }
            );
            
            setAnalyticsData(response.data);
        } catch (err: any) {
            console.error("Error fetching dimension analytics:", err);
            const errorMessage = err?.response?.data?.error || err?.message || "Failed to load analytics";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [entityCollectionId, dimensionFilter]);

    const fetchNeighbours = async () => {
        if (!entityCollectionId) return;
        
        try {
            setLoadingNeighbours(true);
            setNeighboursError(null);
            const response = await db.endpoints.instance.get(
                `/api/entity-collection/${entityCollectionId}/entity-neighbours/`,
                {
                    params: {
                        distance_metric: distanceMetric,
                        top_isolated: topIsolated
                    }
                }
            );
            
            setNeighboursData(response.data);
        } catch (err: any) {
            console.error("Error fetching entity neighbours:", err);
            const errorMessage = err?.response?.data?.error || err?.message || "Failed to load neighbours";
            setNeighboursError(errorMessage);
        } finally {
            setLoadingNeighbours(false);
        }
    };

    const fetchIsolationRankCorrelation = async () => {
        if (!entityCollectionId) return;
        
        try {
            setLoadingCorrelation(true);
            setCorrelationError(null);
            const response = await db.endpoints.instance.get(
                `/api/entity-collection/${entityCollectionId}/entropy-rank-correlation/`,
                {
                    params: {
                        n_samples_per_bin: correlationSamplesPerBin,
                        n_bins: correlationBins,
                        search_type: correlationSearchType,
                        distance_metric: correlationDistanceMetric
                    }
                }
            );
            
            setCorrelationData(response.data);
        } catch (err: any) {
            console.error("Error fetching isolation-rank correlation:", err);
            const errorMessage = err?.response?.data?.error || err?.message || "Failed to load correlation";
            setCorrelationError(errorMessage);
        } finally {
            setLoadingCorrelation(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <IconLoader2 className="animate-spin text-blue-500" size={32} />
                    <span className="text-gray-400">Loading dimension analytics...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="text-red-400">{error}</div>
                <button
                    onClick={fetchAnalytics}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                    <IconRefresh size={16} />
                    Retry
                </button>
            </div>
        );
    }

    if (!analyticsData || analyticsData.dimensions.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-gray-400">No dimensions available for analytics</div>
            </div>
        );
    }

    // Prepare histogram data for Plotly
    const createHistogramData = (distributions: DimensionAnalyticsData['entity_distributions'], titlePrefix: string) => {
        return Object.entries(distributions).map(([dimName, dist]) => {
            if (dist.bins.length === 0 || dist.frequencies.length === 0) {
                return null;
            }
            
            // Calculate bin centers for x-axis
            const binCenters = [];
            for (let i = 0; i < dist.bins.length - 1; i++) {
                binCenters.push((dist.bins[i] + dist.bins[i + 1]) / 2);
            }
            
            return {
                x: binCenters,
                y: dist.frequencies,
                type: 'bar' as const,
                name: dimName,
                title: `${titlePrefix}: ${dimName}`,
            };
        }).filter(Boolean);
    };

    // Prepare type analysis data for pie chart
    const typePieData = {
        labels: Object.keys(analyticsData.type_analysis.counts),
        values: Object.values(analyticsData.type_analysis.counts),
    };

    // Prepare dimension scores data for bar chart
    const sortedScores = Object.entries(analyticsData.dimension_scores)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 20); // Top 20
    
    // Calculate average rank by dimension type (using detailed type if available)
    const dimensionTypeMap = new Map<string, { mainType: string; detailedType: string }>();
    analyticsData.dimensions.forEach(dim => {
        dimensionTypeMap.set(dim.name, {
            mainType: dim.type,
            detailedType: dim.detailed_type || dim.type
        });
    });
    
    // Get all scores sorted by rank (1 = highest score)
    const allScoresRanked = Object.entries(analyticsData.dimension_scores)
        .sort(([, a], [, b]) => b - a)
        .map(([name, score], index) => {
            const typeInfo = dimensionTypeMap.get(name) || { mainType: 'unknown', detailedType: 'unknown' };
            return {
                name,
                score,
                rank: index + 1,
                mainType: typeInfo.mainType,
                detailedType: typeInfo.detailedType
            };
        });
    
    // Calculate average rank by detailed type
    const rankByDetailedType: Record<string, { totalRank: number; count: number; avgRank: number }> = {};
    allScoresRanked.forEach(({ detailedType, rank }) => {
        if (!rankByDetailedType[detailedType]) {
            rankByDetailedType[detailedType] = { totalRank: 0, count: 0, avgRank: 0 };
        }
        rankByDetailedType[detailedType].totalRank += rank;
        rankByDetailedType[detailedType].count += 1;
    });
    
    // Calculate averages
    Object.keys(rankByDetailedType).forEach(type => {
        rankByDetailedType[type].avgRank = rankByDetailedType[type].totalRank / rankByDetailedType[type].count;
    });
    
    // Sort by average rank (lower is better)
    const avgRankByType = Object.entries(rankByDetailedType)
        .map(([type, data]) => ({ type, ...data }))
        .sort((a, b) => a.avgRank - b.avgRank);

    // Prepare weight analytics data for bar chart
    const weightAnalytics = analyticsData.weight_analytics || {};
    const sortedWeights = Object.entries(weightAnalytics)
        .filter(([, data]) => data.average_weight !== null)
        .sort(([, a], [, b]) => (b.average_weight || 0) - (a.average_weight || 0))
        .slice(0, 30); // Top 30
    
    // Prepare box plot data for 5-point statistics (min, Q1, median, Q3, max)
    const boxPlotDimensions = sortedWeights.filter(([, data]) => 
        data.min_weight !== null && 
        data.q1_weight !== null && 
        data.median_weight !== null && 
        data.q3_weight !== null && 
        data.max_weight !== null
    );
    
    // Prepare data for score vs weight scatter plot (filter out non-positive values for log scale)
    const scoreWeightScatterData: { x: number[]; y: number[]; text: string[] } = { x: [], y: [], text: [] };
    Object.entries(analyticsData.entity_distributions).forEach(([dimName, dist]) => {
        const weightData = weightAnalytics[dimName];
        // Only include positive scores for log scale compatibility
        if (dist.mean !== null && dist.mean > 0 && weightData?.average_weight !== null) {
            scoreWeightScatterData.x.push(dist.mean);
            scoreWeightScatterData.y.push(weightData.average_weight);
            scoreWeightScatterData.text.push(dimName);
        }
    });

    return (
        <div className="h-full overflow-y-auto p-6 space-y-6">
            {/* Header with filter toggle */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">Dimension Analytics</h2>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400">Filter:</span>
                    <button
                        onClick={() => setDimensionFilter('live')}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            dimensionFilter === 'live'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                    >
                        Live Collection
                    </button>
                    <button
                        onClick={() => setDimensionFilter('all')}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            dimensionFilter === 'all'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                    >
                        All Dimensions
                    </button>
                    <button
                        onClick={fetchAnalytics}
                        className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                        title="Refresh"
                    >
                        <IconRefresh size={16} />
                    </button>
                </div>
            </div>

            {/* Entity Neighbours Controls Section */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Entity Neighbours & Isolation Analysis</h3>
                <p className="text-sm text-gray-400 mb-4">
                    Find the closest neighbour for each entity and identify the most isolated entities (furthest from their closest neighbour).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Distance Metric
                        </label>
                        <select
                            value={distanceMetric}
                            onChange={(e) => setDistanceMetric(e.target.value as "cosine" | "euclidean" | "manhattan")}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="cosine">Cosine</option>
                            <option value="euclidean">Euclidean</option>
                            <option value="manhattan">Manhattan</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Top Isolated Count
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={topIsolated}
                            onChange={(e) => setTopIsolated(parseInt(e.target.value) || 20)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={fetchNeighbours}
                            disabled={loadingNeighbours}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                        >
                            {loadingNeighbours ? (
                                <>
                                    <IconLoader2 className="animate-spin" size={16} />
                                    <span>Calculating...</span>
                                </>
                            ) : (
                                <span>Calculate Neighbours</span>
                            )}
                        </button>
                    </div>
                </div>
                {neighboursError && (
                    <div className="mt-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                        {neighboursError}
                    </div>
                )}
            </div>

            {/* Type Analysis Section */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Type Analysis</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Main Type Breakdown */}
                    <div>
                        <h4 className="text-md font-medium text-gray-300 mb-3">Main Types</h4>
                        <Plot
                            data={[{
                                labels: typePieData.labels,
                                values: typePieData.values,
                                type: 'pie',
                                marker: {
                                    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444']
                                }
                            }]}
                            layout={{
                                title: 'Dimension Types',
                                paper_bgcolor: 'rgba(0,0,0,0)',
                                plot_bgcolor: 'rgba(0,0,0,0)',
                                font: { color: '#fff' },
                                height: 300,
                            }}
                            config={{ responsive: true, displayModeBar: false }}
                        />
                        <div className="mt-4 space-y-2">
                            {Object.entries(analyticsData.type_analysis.counts).map(([type, count]) => (
                                <div key={type} className="flex items-center justify-between">
                                    <span className="text-gray-300 capitalize">{type}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-white font-medium">{count}</span>
                                        <span className="text-gray-500 text-sm">
                                            ({analyticsData.type_analysis.percentages[type].toFixed(1)}%)
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Detailed Type Breakdown */}
                    <div>
                        <h4 className="text-md font-medium text-gray-300 mb-3">Detailed Types</h4>
                        <Plot
                            data={[{
                                labels: Object.keys(analyticsData.type_analysis.detailed_counts),
                                values: Object.values(analyticsData.type_analysis.detailed_counts),
                                type: 'pie',
                                marker: {
                                    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6']
                                }
                            }]}
                            layout={{
                                title: 'Detailed Dimension Types',
                                paper_bgcolor: 'rgba(0,0,0,0)',
                                plot_bgcolor: 'rgba(0,0,0,0)',
                                font: { color: '#fff' },
                                height: 300,
                            }}
                            config={{ responsive: true, displayModeBar: false }}
                        />
                        <div className="mt-4 space-y-2">
                            {Object.entries(analyticsData.type_analysis.detailed_counts)
                                .sort(([, a], [, b]) => b - a)
                                .map(([type, count]) => (
                                <div key={type} className="flex items-center justify-between">
                                    <span className="text-gray-300 capitalize">{type.replace('_', ' ')}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-white font-medium">{count}</span>
                                        <span className="text-gray-500 text-sm">
                                            ({analyticsData.type_analysis.detailed_percentages[type].toFixed(1)}%)
                                        </span>
                                    </div>
                                </div>
                            ))}
                            <div className="pt-2 border-t border-gray-700">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-300 font-medium">Total</span>
                                    <span className="text-white font-bold">{analyticsData.type_analysis.total}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dimension Scores Section */}
            {sortedScores.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Dimension Scores (from last Oasis Index)</h3>
                    
                    {/* Average Rank by Type */}
                    {avgRankByType.length > 0 && (
                        <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                            <h4 className="text-md font-medium text-gray-300 mb-3">Average Rank by Type</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {avgRankByType.map(({ type, avgRank, count }) => (
                                    <div key={type} className="flex items-center justify-between p-2 bg-gray-800 rounded">
                                        <span className="text-gray-300 capitalize text-sm">{type}</span>
                                        <div className="flex items-center gap-3">
                                            <span className="text-white font-medium text-sm">
                                                Rank: {avgRank.toFixed(1)}
                                            </span>
                                            <span className="text-gray-500 text-xs">
                                                (n={count})
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-3">
                                Lower rank = higher score. Rank 1 is the highest scoring dimension.
                            </p>
                        </div>
                    )}
                    
                    <div className="max-h-96 overflow-y-auto">
                        <Plot
                            data={[{
                                x: sortedScores.map(([name]) => name),
                                y: sortedScores.map(([, score]) => score),
                                type: 'bar',
                                marker: {
                                    color: sortedScores.map(([, score]) => score),
                                    colorscale: 'Viridis',
                                }
                            }]}
                            layout={{
                                xaxis: { title: 'Dimension', tickangle: -45 },
                                yaxis: { title: 'Score' },
                                paper_bgcolor: 'rgba(0,0,0,0)',
                                plot_bgcolor: 'rgba(0,0,0,0)',
                                font: { color: '#fff' },
                                height: Math.max(400, sortedScores.length * 30),
                                margin: { b: 150 },
                            }}
                            config={{ responsive: true, displayModeBar: false }}
                        />
                    </div>
                </div>
            )}

            {/* Weight Analytics Section */}
            {sortedWeights.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Query Weight Analytics</h3>
                    
                    {/* Weight by Rank Position - Shows how concentrated weights are */}
                    {analyticsData.weight_by_rank_stats && analyticsData.weight_by_rank_stats.length > 0 && (
                        <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                            <h4 className="text-md font-medium text-gray-300 mb-2">Weight by Rank Position</h4>
                            <p className="text-xs text-gray-500 mb-4">
                                Shows the average weight assigned to the top-1, top-2, etc. dimension across all queries.
                                Reveals how concentrated weights are - steep drop = few dimensions matter, flat = weights spread evenly.
                            </p>
                            <Plot
                                data={[
                                    // IQR range (Q1 to Q3) as filled area
                                    {
                                        type: 'scatter',
                                        x: analyticsData.weight_by_rank_stats.map(s => `Top ${s.rank}`),
                                        y: analyticsData.weight_by_rank_stats.map(s => s.q3),
                                        mode: 'lines',
                                        line: { color: 'transparent' },
                                        showlegend: false,
                                        hoverinfo: 'skip'
                                    },
                                    {
                                        type: 'scatter',
                                        x: analyticsData.weight_by_rank_stats.map(s => `Top ${s.rank}`),
                                        y: analyticsData.weight_by_rank_stats.map(s => s.q1),
                                        mode: 'lines',
                                        fill: 'tonexty',
                                        fillcolor: 'rgba(139, 92, 246, 0.3)',
                                        line: { color: 'rgba(139, 92, 246, 0.5)' },
                                        name: 'IQR (Q1-Q3)',
                                        hovertemplate: 'Q1: %{y:.1f}<extra></extra>'
                                    },
                                    // Min-Max range as error bars on median
                                    {
                                        type: 'scatter',
                                        mode: 'markers',
                                        x: analyticsData.weight_by_rank_stats.map(s => `Top ${s.rank}`),
                                        y: analyticsData.weight_by_rank_stats.map(s => s.median),
                                        error_y: {
                                            type: 'data',
                                            symmetric: false,
                                            array: analyticsData.weight_by_rank_stats.map(s => s.max - s.median),
                                            arrayminus: analyticsData.weight_by_rank_stats.map(s => s.median - s.min),
                                            color: 'rgba(167, 139, 250, 0.6)',
                                            thickness: 1,
                                            width: 6
                                        },
                                        marker: { size: 1, color: 'transparent' },
                                        showlegend: false,
                                        hoverinfo: 'skip'
                                    },
                                    // Line for mean
                                    {
                                        type: 'scatter',
                                        mode: 'lines+markers',
                                        x: analyticsData.weight_by_rank_stats.map(s => `Top ${s.rank}`),
                                        y: analyticsData.weight_by_rank_stats.map(s => s.mean),
                                        name: 'Mean',
                                        line: { color: '#10b981', width: 3 },
                                        marker: { size: 8, color: '#10b981' },
                                        hovertemplate: '%{x}<br>Mean: %{y:.1f}<extra></extra>'
                                    },
                                    // Line for median
                                    {
                                        type: 'scatter',
                                        mode: 'lines+markers',
                                        x: analyticsData.weight_by_rank_stats.map(s => `Top ${s.rank}`),
                                        y: analyticsData.weight_by_rank_stats.map(s => s.median),
                                        name: 'Median',
                                        line: { color: '#f59e0b', width: 2, dash: 'dash' },
                                        marker: { size: 6, color: '#f59e0b', symbol: 'diamond' },
                                        hovertemplate: '%{x}<br>Median: %{y:.1f}<extra></extra>'
                                    }
                                ]}
                                layout={{
                                    xaxis: { 
                                        title: 'Rank Position (Highest Weight = Top 1)', 
                                        tickangle: -45,
                                        gridcolor: 'rgba(255,255,255,0.1)'
                                    },
                                    yaxis: { 
                                        title: 'Weight', 
                                        gridcolor: 'rgba(255,255,255,0.1)',
                                        rangemode: 'tozero'
                                    },
                                    paper_bgcolor: 'rgba(0,0,0,0)',
                                    plot_bgcolor: 'rgba(0,0,0,0)',
                                    font: { color: '#fff' },
                                    height: 350,
                                    margin: { b: 80, t: 20 },
                                    showlegend: true,
                                    legend: { x: 1, y: 1, xanchor: 'right', bgcolor: 'rgba(0,0,0,0.5)' }
                                }}
                                config={{ responsive: true, displayModeBar: false }}
                            />
                            <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                                {analyticsData.weight_by_rank_stats.slice(0, 6).map(stat => (
                                    <div key={stat.rank} className="bg-gray-800 rounded p-2 text-center">
                                        <div className="text-purple-400 font-semibold">Top {stat.rank}</div>
                                        <div className="text-white">{stat.mean.toFixed(1)}</div>
                                        <div className="text-gray-500">avg (n={stat.count})</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Score-Weight Correlation */}
                    {analyticsData.score_weight_correlation !== null && (
                        <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-md font-medium text-gray-300">Score vs Weight Correlation</h4>
                                <div className="flex items-center gap-4">
                                    <span className={`text-xl font-bold ${
                                        analyticsData.score_weight_correlation > 0.5 ? 'text-green-400' :
                                        analyticsData.score_weight_correlation > 0 ? 'text-yellow-400' :
                                        analyticsData.score_weight_correlation > -0.5 ? 'text-orange-400' :
                                        'text-red-400'
                                    }`}>
                                        r = {analyticsData.score_weight_correlation.toFixed(3)}
                                    </span>
                                    {analyticsData.score_weight_correlation_pvalue !== null && (
                                        <span className={`text-sm font-medium px-2 py-1 rounded ${
                                            analyticsData.score_weight_correlation_pvalue < 0.001 ? 'bg-green-900 text-green-300' :
                                            analyticsData.score_weight_correlation_pvalue < 0.01 ? 'bg-green-800 text-green-300' :
                                            analyticsData.score_weight_correlation_pvalue < 0.05 ? 'bg-yellow-900 text-yellow-300' :
                                            'bg-gray-700 text-gray-400'
                                        }`}>
                                            {analyticsData.score_weight_correlation_pvalue < 0.001 ? 'p < 0.001 ✓✓✓' :
                                             analyticsData.score_weight_correlation_pvalue < 0.01 ? `p = ${analyticsData.score_weight_correlation_pvalue.toFixed(3)} ✓✓` :
                                             analyticsData.score_weight_correlation_pvalue < 0.05 ? `p = ${analyticsData.score_weight_correlation_pvalue.toFixed(3)} ✓` :
                                             `p = ${analyticsData.score_weight_correlation_pvalue.toFixed(3)}`}
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-500">
                                        n = {analyticsData.score_weight_correlation_n}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                                <p>
                                    Pearson correlation between entity dimension scores and average query weights.
                                    Positive values indicate dimensions with higher entity scores also get higher weights in queries.
                                </p>
                                {analyticsData.score_weight_correlation_pvalue !== null && (
                                    <p className={
                                        analyticsData.score_weight_correlation_pvalue < 0.05 ? 'text-green-400' : 'text-gray-400'
                                    }>
                                        {analyticsData.score_weight_correlation_pvalue < 0.001 ? 
                                            'Highly significant correlation (p < 0.001) - very unlikely to be due to chance.' :
                                         analyticsData.score_weight_correlation_pvalue < 0.01 ? 
                                            'Significant correlation (p < 0.01) - unlikely to be due to chance.' :
                                         analyticsData.score_weight_correlation_pvalue < 0.05 ? 
                                            'Significant correlation (p < 0.05) - reasonably unlikely to be due to chance.' :
                                            'Not statistically significant (p ≥ 0.05) - could be due to random chance.'}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    
                   
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Average Weight Bar Chart */}
                        <div>
                            <h4 className="text-md font-medium text-gray-300 mb-3">Average Weight by Dimension</h4>
                            <div className="max-h-96 overflow-y-auto">
                                <Plot
                                    data={[{
                                        x: sortedWeights.map(([name]) => name),
                                        y: sortedWeights.map(([, data]) => data.average_weight || 0),
                                        type: 'bar',
                                        marker: {
                                            color: sortedWeights.map(([, data]) => data.average_weight || 0),
                                            colorscale: 'Plasma',
                                        },
                                        text: sortedWeights.map(([, data]) => `n=${data.count}`),
                                        hovertemplate: '%{x}<br>Weight: %{y:.3f}<br>%{text}<extra></extra>'
                                    }]}
                                    layout={{
                                        xaxis: { title: 'Dimension', tickangle: -45 },
                                        yaxis: { title: 'Average Weight' },
                                        paper_bgcolor: 'rgba(0,0,0,0)',
                                        plot_bgcolor: 'rgba(0,0,0,0)',
                                        font: { color: '#fff' },
                                        height: Math.max(400, sortedWeights.length * 20),
                                        margin: { b: 150 },
                                    }}
                                    config={{ responsive: true, displayModeBar: false }}
                                />
                            </div>
                        </div>
                        
                        {/* Score vs Weight Scatter Plot */}
                        {scoreWeightScatterData.x.length > 0 && (
                            <div>
                                <h4 className="text-md font-medium text-gray-300 mb-3">Entity Score vs Query Weight</h4>
                                <Plot
                                    data={[{
                                        x: scoreWeightScatterData.x,
                                        y: scoreWeightScatterData.y,
                                        type: 'scatter',
                                        mode: 'markers',
                                        text: scoreWeightScatterData.text,
                                        marker: {
                                            size: 10,
                                            color: '#3b82f6',
                                            opacity: 0.7
                                        },
                                        hovertemplate: '%{text}<br>Entity Mean: %{x:.3f}<br>Avg Weight: %{y:.3f}<extra></extra>'
                                    }]}
                                    layout={{
                                        xaxis: { title: 'Entity Mean Score (log scale)', type: 'log', gridcolor: 'rgba(255,255,255,0.1)' },
                                        yaxis: { title: 'Average Query Weight', gridcolor: 'rgba(255,255,255,0.1)' },
                                        paper_bgcolor: 'rgba(0,0,0,0)',
                                        plot_bgcolor: 'rgba(0,0,0,0)',
                                        font: { color: '#fff' },
                                        height: 400,
                                    }}
                                    config={{ responsive: true, displayModeBar: false }}
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* Weight Stats Table */}
                    <div className="mt-6">
                        <h4 className="text-md font-medium text-gray-300 mb-3">Weight Statistics by Dimension (5-Point Summary)</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-gray-400 border-b border-gray-700">
                                        <th className="text-left py-2 px-3">Dimension</th>
                                        <th className="text-right py-2 px-3">Mean</th>
                                        <th className="text-right py-2 px-3">Min</th>
                                        <th className="text-right py-2 px-3">Q1</th>
                                        <th className="text-right py-2 px-3">Median</th>
                                        <th className="text-right py-2 px-3">Q3</th>
                                        <th className="text-right py-2 px-3">Max</th>
                                        <th className="text-right py-2 px-3">Std</th>
                                        <th className="text-right py-2 px-3">n</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedWeights.slice(0, 20).map(([dimName, data]) => (
                                        <tr key={dimName} className="border-b border-gray-800 hover:bg-gray-750">
                                            <td className="py-2 px-3 text-white truncate max-w-xs" title={dimName}>{dimName}</td>
                                            <td className="py-2 px-3 text-right text-blue-400 font-medium">{data.average_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-gray-500">{data.min_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-purple-400">{data.q1_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-green-400 font-medium">{data.median_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-purple-400">{data.q3_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-gray-500">{data.max_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-gray-600">{data.std_weight?.toFixed(1) ?? '-'}</td>
                                            <td className="py-2 px-3 text-right text-gray-500">{data.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Combined Entity and Query Distributions Section */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Entity & Query Distributions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
                    {Object.entries(analyticsData.entity_distributions).map(([dimName, entityDist]) => {
                        const queryDist = analyticsData.query_distributions[dimName];
                        if (entityDist.bins.length === 0 && (!queryDist || queryDist.bins.length === 0)) return null;
                        
                        // Get dimension metadata
                        const dimension = analyticsData.dimensions.find(d => d.name === dimName);
                        const detailedType = dimension?.detailed_type || dimension?.type || 'unknown';
                        const args = dimension?.args || {};
                        const score = analyticsData.dimension_scores[dimName];
                        const weightData = weightAnalytics[dimName];
                        const entityEntropy = analyticsData.entity_entropy?.[dimName];
                        const queryEntropyVal = analyticsData.query_entropy?.[dimName];
                        
                        // Calculate bin centers for entity distribution
                        const entityBinCenters = [];
                        if (entityDist.bins.length > 0) {
                            for (let i = 0; i < entityDist.bins.length - 1; i++) {
                                entityBinCenters.push((entityDist.bins[i] + entityDist.bins[i + 1]) / 2);
                            }
                        }
                        
                        // Calculate bin centers for query distribution
                        const queryBinCenters = [];
                        if (queryDist && queryDist.bins.length > 0) {
                            for (let i = 0; i < queryDist.bins.length - 1; i++) {
                                queryBinCenters.push((queryDist.bins[i] + queryDist.bins[i + 1]) / 2);
                            }
                        }
                        
                        // Prepare plot data
                        const plotData: any[] = [];
                        if (entityDist.bins.length > 0) {
                            plotData.push({
                                x: entityBinCenters,
                                y: entityDist.frequencies,
                                type: 'bar',
                                name: 'Entities',
                                marker: { color: '#3b82f6' }
                            });
                        }
                        if (queryDist && queryDist.bins.length > 0) {
                            plotData.push({
                                x: queryBinCenters,
                                y: queryDist.frequencies,
                                type: 'bar',
                                name: 'Queries',
                                marker: { color: '#10b981' }
                            });
                        }
                        
                        // Format args for display
                        const formatArgs = (args: Record<string, any>): string => {
                            if (!args || Object.keys(args).length === 0) return 'None';
                            const entries = Object.entries(args).map(([key, value]) => {
                                if (key === 'value_mapping' && typeof value === 'object' && value !== null) {
                                    const mappingKeys = Object.keys(value);
                                    if (mappingKeys.length > 3) {
                                        return `${key}: {${mappingKeys.slice(0, 3).join(', ')}, ...} (${mappingKeys.length} items)`;
                                    }
                                    return `${key}: ${JSON.stringify(value)}`;
                                }
                                if (Array.isArray(value)) {
                                    if (value.length > 5) {
                                        return `${key}: [${value.slice(0, 5).join(', ')}, ...] (${value.length} items)`;
                                    }
                                    return `${key}: [${value.join(', ')}]`;
                                }
                                if (typeof value === 'object' && value !== null) {
                                    const objKeys = Object.keys(value);
                                    if (objKeys.length > 3) {
                                        return `${key}: {${objKeys.slice(0, 3).join(', ')}, ...} (${objKeys.length} keys)`;
                                    }
                                    return `${key}: ${JSON.stringify(value)}`;
                                }
                                const strValue = String(value);
                                if (strValue.length > 50) {
                                    return `${key}: ${strValue.substring(0, 50)}...`;
                                }
                                return `${key}: ${value}`;
                            });
                            return entries.join('; ');
                        };
                        
                        return (
                            <div key={dimName} className="bg-gray-900 rounded p-4 border border-gray-700 flex flex-col" style={{ minHeight: 0 }}>
                                <div className="flex-shrink-0 mb-3">
                                    <h4 className="text-sm font-medium text-white mb-2 truncate" title={dimName}>
                                        {dimName}
                                    </h4>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-400">Type:</span>
                                            <span className="text-blue-400 capitalize">{detailedType.replace(/_/g, ' ')}</span>
                                        </div>
                                        {score !== undefined && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-400">Score:</span>
                                                <span className="text-green-400 font-medium">{score.toFixed(4)}</span>
                                            </div>
                                        )}
                                        {weightData && weightData.average_weight !== null && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-400">Avg Weight:</span>
                                                <span className="text-purple-400 font-medium">{weightData.average_weight.toFixed(3)}</span>
                                                <span className="text-gray-500 text-xs">(n={weightData.count})</span>
                                            </div>
                                        )}
                                        {entityEntropy !== null && entityEntropy !== undefined && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-400">Entropy:</span>
                                                <span className={`font-medium ${
                                                    entityEntropy > 3 ? 'text-green-400' : 
                                                    entityEntropy > 2 ? 'text-yellow-400' : 
                                                    'text-red-400'
                                                }`}>{entityEntropy.toFixed(2)} bits</span>
                                                {queryEntropyVal !== null && queryEntropyVal !== undefined && (
                                                    <span className="text-gray-500 text-xs">(Q: {queryEntropyVal.toFixed(2)})</span>
                                                )}
                                            </div>
                                        )}
                                        {Object.keys(args).length > 0 && (
                                            <div className="flex items-start gap-2">
                                                <span className="text-gray-400 flex-shrink-0">Args:</span>
                                                <span className="text-gray-300 text-xs break-words" title={formatArgs(args)}>
                                                    {formatArgs(args)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1 min-h-0" style={{ height: '250px', maxHeight: '250px' }}>
                                    {plotData.length > 0 ? (
                                        <Plot
                                            data={plotData}
                                            layout={{
                                                height: 250,
                                                margin: { t: 10, b: 35, l: 45, r: 10 },
                                                paper_bgcolor: 'rgba(0,0,0,0)',
                                                plot_bgcolor: 'rgba(0,0,0,0)',
                                                font: { color: '#fff', size: 9 },
                                                xaxis: { 
                                                    title: 'Value', 
                                                    gridcolor: 'rgba(255,255,255,0.1)',
                                                    titlefont: { size: 9 }
                                                },
                                                yaxis: { 
                                                    title: 'Frequency', 
                                                    gridcolor: 'rgba(255,255,255,0.1)',
                                                    titlefont: { size: 9 }
                                                },
                                                barmode: 'group',
                                                legend: {
                                                    x: 0.5,
                                                    y: -0.18,
                                                    xanchor: 'center',
                                                    orientation: 'h',
                                                    font: { color: '#fff', size: 9 }
                                                },
                                                showlegend: plotData.length > 1
                                            }}
                                            config={{ responsive: true, displayModeBar: false }}
                                            style={{ width: '100%', height: '100%', maxHeight: '250px' }}
                                            useResizeHandler={true}
                                        />
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-gray-400 mt-3 space-y-1 flex-shrink-0">
                                    {entityDist.count > 0 && (
                                        <div>
                                            Entities: {entityDist.count} | Mean: {entityDist.mean?.toFixed(2) ?? 'N/A'} | Std: {entityDist.std?.toFixed(2) ?? 'N/A'}
                                        </div>
                                    )}
                                    {queryDist && queryDist.count > 0 && (
                                        <div>
                                            Queries: {queryDist.count} | Mean: {queryDist.mean?.toFixed(2) ?? 'N/A'} | Std: {queryDist.std?.toFixed(2) ?? 'N/A'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Correlation Matrices Section */}
            {analyticsData.entity_correlation_matrix.matrix.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Entity Correlation Matrix</h3>
                    <div className="h-[600px]">
                        <CorrelationMatrix
                            data={{
                                matrix: analyticsData.entity_correlation_matrix.matrix,
                                labels: analyticsData.entity_correlation_matrix.labels,
                            }}
                            title="Entity Dimension Correlations"
                            xAxisLabel="Dimension"
                            yAxisLabel="Dimension"
                            colorscale="RdBu"
                            showText={false}
                        />
                    </div>
                </div>
            )}

            {analyticsData.query_correlation_matrix.matrix.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Query Correlation Matrix</h3>
                    <div className="h-[600px]">
                        <CorrelationMatrix
                            data={{
                                matrix: analyticsData.query_correlation_matrix.matrix,
                                labels: analyticsData.query_correlation_matrix.labels,
                            }}
                            title="Query Dimension Correlations"
                            xAxisLabel="Dimension"
                            yAxisLabel="Dimension"
                            colorscale="RdBu"
                            showText={false}
                        />
                    </div>
                </div>
            )}

            {/* Information Theory Section */}
            {analyticsData.entity_entropy && Object.keys(analyticsData.entity_entropy).length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-2">Dimension Information Theory Metrics</h3>
                    <p className="text-sm text-gray-400 mb-4">
                        Per-dimension Shannon entropy measures how spread out each dimension's classifications are across all entities/queries.
                        Higher entropy = dimension spreads values more evenly = better discriminative power. Max entropy for 20 bins ≈ 4.32 bits.
                    </p>
                    
                    {/* Entropy Bar Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Dimension Entropy from Entity Data */}
                        <div>
                            <h4 className="text-md font-medium text-gray-300 mb-3">Dimension Entropy from Entity Data (bits)</h4>
                            {(() => {
                                const sortedEntropy = Object.entries(analyticsData.entity_entropy)
                                    .filter(([, val]) => val !== null)
                                    .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));
                                
                                return (
                                    <Plot
                                        data={[{
                                            x: sortedEntropy.map(([name]) => name),
                                            y: sortedEntropy.map(([, val]) => val ?? 0),
                                            type: 'bar',
                                            marker: {
                                                color: sortedEntropy.map(([, val]) => val ?? 0),
                                                colorscale: 'Viridis',
                                                cmin: 0,
                                                cmax: 4.32, // Max entropy for 20 bins
                                            },
                                            hovertemplate: '%{x}<br>Entropy: %{y:.3f} bits<extra></extra>'
                                        }]}
                                        layout={{
                                            xaxis: { title: 'Dimension', tickangle: -45 },
                                            yaxis: { title: 'Entropy (bits)', range: [0, 4.5] },
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            font: { color: '#fff' },
                                            height: 350,
                                            margin: { b: 120 },
                                        }}
                                        config={{ responsive: true, displayModeBar: false }}
                                    />
                                );
                            })()}
                        </div>
                        
                        {/* Dimension Entropy from Query Data */}
                        <div>
                            <h4 className="text-md font-medium text-gray-300 mb-3">Dimension Entropy from Query Data (bits)</h4>
                            {(() => {
                                const sortedEntropy = Object.entries(analyticsData.query_entropy || {})
                                    .filter(([, val]) => val !== null)
                                    .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));
                                
                                if (sortedEntropy.length === 0) {
                                    return <div className="text-gray-500 text-sm">No query entropy data available</div>;
                                }
                                
                                return (
                                    <Plot
                                        data={[{
                                            x: sortedEntropy.map(([name]) => name),
                                            y: sortedEntropy.map(([, val]) => val ?? 0),
                                            type: 'bar',
                                            marker: {
                                                color: sortedEntropy.map(([, val]) => val ?? 0),
                                                colorscale: 'Plasma',
                                                cmin: 0,
                                                cmax: 4.32,
                                            },
                                            hovertemplate: '%{x}<br>Entropy: %{y:.3f} bits<extra></extra>'
                                        }]}
                                        layout={{
                                            xaxis: { title: 'Dimension', tickangle: -45 },
                                            yaxis: { title: 'Entropy (bits)', range: [0, 4.5] },
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            font: { color: '#fff' },
                                            height: 350,
                                            margin: { b: 120 },
                                        }}
                                        config={{ responsive: true, displayModeBar: false }}
                                    />
                                );
                            })()}
                        </div>
                    </div>
                    
                    {/* Entropy Stats Summary */}
                    <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                        <h4 className="text-md font-medium text-gray-300 mb-3">Entropy Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {(() => {
                                const entityEntropies = Object.values(analyticsData.entity_entropy).filter(v => v !== null) as number[];
                                const queryEntropies = Object.values(analyticsData.query_entropy || {}).filter(v => v !== null) as number[];
                                
                                const avgEntityEntropy = entityEntropies.length > 0 
                                    ? entityEntropies.reduce((a, b) => a + b, 0) / entityEntropies.length 
                                    : 0;
                                const avgQueryEntropy = queryEntropies.length > 0 
                                    ? queryEntropies.reduce((a, b) => a + b, 0) / queryEntropies.length 
                                    : 0;
                                const maxEntityEntropy = entityEntropies.length > 0 ? Math.max(...entityEntropies) : 0;
                                const minEntityEntropy = entityEntropies.length > 0 ? Math.min(...entityEntropies) : 0;
                                
                                return (
                                    <>
                                        <div className="bg-gray-800 rounded p-3 text-center">
                                            <div className="text-xs text-gray-400">Avg Dimension Entropy</div>
                                            <div className="text-xs text-gray-500">(Entity Data)</div>
                                            <div className="text-lg font-bold text-blue-400">{avgEntityEntropy.toFixed(2)} bits</div>
                                        </div>
                                        <div className="bg-gray-800 rounded p-3 text-center">
                                            <div className="text-xs text-gray-400">Avg Dimension Entropy</div>
                                            <div className="text-xs text-gray-500">(Query Data)</div>
                                            <div className="text-lg font-bold text-green-400">{avgQueryEntropy.toFixed(2)} bits</div>
                                        </div>
                                        <div className="bg-gray-800 rounded p-3 text-center">
                                            <div className="text-xs text-gray-400">Max Dimension Entropy</div>
                                            <div className="text-xs text-gray-500">(Entity Data)</div>
                                            <div className="text-lg font-bold text-purple-400">{maxEntityEntropy.toFixed(2)} bits</div>
                                        </div>
                                        <div className="bg-gray-800 rounded p-3 text-center">
                                            <div className="text-xs text-gray-400">Min Dimension Entropy</div>
                                            <div className="text-xs text-gray-500">(Entity Data)</div>
                                            <div className="text-lg font-bold text-orange-400">{minEntityEntropy.toFixed(2)} bits</div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            Per-dimension entropy measures how spread out each dimension's values are across all entities/queries. Higher = better discriminative power.
                        </p>
                    </div>
                </div>
            )}

            {/* Mutual Information Matrix */}
            {analyticsData.entity_mutual_information_matrix?.matrix?.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-2">Mutual Information Matrix (Entity Data)</h3>
                    <p className="text-sm text-gray-400 mb-4">
                        I(X;Y) measures shared information between dimensions. Lower values indicate more orthogonal/independent dimensions.
                        Diagonal shows entropy H(X) since I(X;X) = H(X).
                    </p>
                    <div className="h-[600px]">
                        <CorrelationMatrix
                            data={{
                                matrix: analyticsData.entity_mutual_information_matrix.matrix,
                                labels: analyticsData.entity_mutual_information_matrix.labels,
                            }}
                            title="Entity Mutual Information I(X;Y)"
                            xAxisLabel="Dimension"
                            yAxisLabel="Dimension"
                            colorscale="Viridis"
                            showText={false}
                        />
                    </div>
                </div>
            )}

            {analyticsData.query_mutual_information_matrix?.matrix?.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-2">Mutual Information Matrix (Query Data)</h3>
                    <p className="text-sm text-gray-400 mb-4">
                        Mutual information between dimensions based on query classifications.
                    </p>
                    <div className="h-[600px]">
                        <CorrelationMatrix
                            data={{
                                matrix: analyticsData.query_mutual_information_matrix.matrix,
                                labels: analyticsData.query_mutual_information_matrix.labels,
                            }}
                            title="Query Mutual Information I(X;Y)"
                            xAxisLabel="Dimension"
                            yAxisLabel="Dimension"
                            colorscale="Plasma"
                            showText={false}
                        />
                    </div>
                </div>
            )}

            {/* Conditional Entropy Matrix */}
            {analyticsData.entity_conditional_entropy_matrix?.matrix?.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-2">Conditional Entropy Matrix (Entity Data)</h3>
                    <p className="text-sm text-gray-400 mb-4">
                        H(X|Y) = uncertainty about X after knowing Y. High values mean X is independent of Y.
                        Row X, Column Y shows H(X|Y). Diagonal is 0 since H(X|X) = 0.
                    </p>
                    <div className="h-[600px]">
                        <CorrelationMatrix
                            data={{
                                matrix: analyticsData.entity_conditional_entropy_matrix.matrix,
                                labels: analyticsData.entity_conditional_entropy_matrix.labels,
                            }}
                            title="Entity Conditional Entropy H(X|Y)"
                            xAxisLabel="Y (Given)"
                            yAxisLabel="X (Unknown)"
                            colorscale="Cividis"
                            showText={false}
                        />
                    </div>
                </div>
            )}

            {analyticsData.query_conditional_entropy_matrix?.matrix?.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <h3 className="text-lg font-semibold text-white mb-2">Conditional Entropy Matrix (Query Data)</h3>
                    <p className="text-sm text-gray-400 mb-4">
                        Conditional entropy between dimensions based on query classifications.
                    </p>
                    <div className="h-[600px]">
                        <CorrelationMatrix
                            data={{
                                matrix: analyticsData.query_conditional_entropy_matrix.matrix,
                                labels: analyticsData.query_conditional_entropy_matrix.labels,
                            }}
                            title="Query Conditional Entropy H(X|Y)"
                            xAxisLabel="Y (Given)"
                            yAxisLabel="X (Unknown)"
                            colorscale="Inferno"
                            showText={false}
                        />
                    </div>
                </div>
            )}

            {/* Isolation-Rank Correlation Analysis */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-2">Entity Isolation vs Search Rank</h3>
                <p className="text-sm text-gray-400 mb-4">
                    Analyze whether entities that are more isolated (unique) in the embedding space are easier or harder 
                    to find in search. Isolation is measured as distance to the nearest neighbor - entities with high 
                    isolation don't have similar entities nearby.
                </p>
                
                {/* Controls */}
                <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-gray-900 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Search Type:</label>
                        <select
                            value={correlationSearchType}
                            onChange={(e) => setCorrelationSearchType(e.target.value as "oasis" | "semantic")}
                            className="px-3 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                        >
                            <option value="oasis">Oasis</option>
                            <option value="semantic">Semantic</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Distance Metric:</label>
                        <select
                            value={correlationDistanceMetric}
                            onChange={(e) => setCorrelationDistanceMetric(e.target.value as "cosine" | "euclidean" | "manhattan")}
                            className="px-3 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                        >
                            <option value="cosine">Cosine</option>
                            <option value="euclidean">Euclidean</option>
                            <option value="manhattan">Manhattan</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Samples per bin:</label>
                        <input
                            type="number"
                            value={correlationSamplesPerBin}
                            onChange={(e) => setCorrelationSamplesPerBin(parseInt(e.target.value) || 10)}
                            min={1}
                            max={50}
                            className="w-16 px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Bins:</label>
                        <input
                            type="number"
                            value={correlationBins}
                            onChange={(e) => setCorrelationBins(parseInt(e.target.value) || 5)}
                            min={2}
                            max={10}
                            className="w-16 px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                        />
                    </div>
                    <button
                        onClick={fetchIsolationRankCorrelation}
                        disabled={loadingCorrelation}
                        className="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    >
                        {loadingCorrelation ? (
                            <>
                                <IconLoader2 className="animate-spin" size={16} />
                                Running Analysis...
                            </>
                        ) : (
                            <>
                                <IconRefresh size={16} />
                                Run Correlation Analysis
                            </>
                        )}
                    </button>
                </div>

                {correlationError && (
                    <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                        {correlationError}
                    </div>
                )}

                {correlationData && (
                    <div className="space-y-4">
                        {/* Correlation Result Summary */}
                        <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
                            <div className="flex flex-wrap gap-6 mb-4">
                                <div className="text-center">
                                    <div className={`text-2xl font-bold ${
                                        correlationData.correlation !== null
                                            ? correlationData.correlation < -0.3 
                                                ? 'text-green-400' 
                                                : correlationData.correlation > 0.3 
                                                    ? 'text-orange-400' 
                                                    : 'text-yellow-400'
                                            : 'text-gray-500'
                                    }`}>
                                        {correlationData.correlation !== null 
                                            ? correlationData.correlation.toFixed(3) 
                                            : 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-400">Spearman ρ</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-white">
                                        {correlationData.pvalue !== null 
                                            ? correlationData.pvalue < 0.001 
                                                ? '<0.001' 
                                                : correlationData.pvalue.toFixed(3)
                                            : 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-400">p-value</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-white">
                                        {correlationData.n_samples}
                                    </div>
                                    <div className="text-xs text-gray-400">Valid Samples</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-white">
                                        {correlationData.summary_stats?.mean_rank?.toFixed(1) || 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-400">Mean Rank</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-white">
                                        {correlationData.summary_stats?.mean_isolation?.toFixed(4) || 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-400">Mean Isolation</div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-300 italic">
                                {correlationData.interpretation}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                                Using {correlationData.distance_metric} distance metric
                            </p>
                        </div>

                        {/* Scatter Plot */}
                        {correlationData.samples.filter(s => s.rank !== null).length > 0 && (
                            <div className="h-[400px]">
                                <Plot
                                    data={[{
                                        x: correlationData.samples
                                            .filter(s => s.rank !== null)
                                            .map(s => s.isolation),
                                        y: correlationData.samples
                                            .filter(s => s.rank !== null)
                                            .map(s => s.rank),
                                        text: correlationData.samples
                                            .filter(s => s.rank !== null)
                                            .map(s => `Entity: ${s.entity_id.substring(0, 8)}...\nQuery: ${s.query?.substring(0, 50) || 'N/A'}...`),
                                        mode: 'markers',
                                        type: 'scatter',
                                        marker: {
                                            size: 10,
                                            color: correlationData.samples
                                                .filter(s => s.rank !== null)
                                                .map(s => s.rank as number),
                                            colorscale: 'RdYlGn_r',
                                            colorbar: {
                                                title: 'Rank'
                                            },
                                            line: {
                                                color: '#1f2937',
                                                width: 1
                                            }
                                        },
                                        hovertemplate: '%{text}<br>Isolation: %{x:.4f}<br>Rank: %{y}<extra></extra>'
                                    }]}
                                    layout={{
                                        title: {
                                            text: 'Entity Isolation vs Search Rank',
                                            font: { color: '#ffffff', size: 16 }
                                        },
                                        xaxis: {
                                            title: { text: 'Isolation (Distance to Nearest Neighbor)', font: { color: '#9ca3af' } },
                                            color: '#9ca3af',
                                            gridcolor: '#374151',
                                            zerolinecolor: '#4b5563'
                                        },
                                        yaxis: {
                                            title: { text: 'Search Rank (lower is better)', font: { color: '#9ca3af' } },
                                            color: '#9ca3af',
                                            gridcolor: '#374151',
                                            zerolinecolor: '#4b5563',
                                            autorange: 'reversed'
                                        },
                                        paper_bgcolor: 'transparent',
                                        plot_bgcolor: 'transparent',
                                        margin: { t: 50, b: 60, l: 60, r: 30 },
                                        showlegend: false
                                    }}
                                    config={{ responsive: true }}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        )}

                        {/* Bin Info */}
                        {correlationData.bin_info && correlationData.bin_info.length > 0 && (
                            <div>
                                <h4 className="text-md font-medium text-gray-300 mb-2">Sampling Distribution</h4>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                    {correlationData.bin_info.map((bin) => (
                                        <div key={bin.bin} className="p-3 bg-gray-900 rounded border border-gray-700 text-center">
                                            <div className="text-xs text-gray-500 mb-1">Bin {bin.bin}</div>
                                            <div className="text-sm text-white font-medium">
                                                {bin.range[0].toFixed(3)} - {bin.range[1].toFixed(3)}
                                            </div>
                                            <div className="text-xs text-gray-400 mt-1">
                                                {bin.sampled} / {bin.total_in_bin} sampled
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Sample Details Table */}
                        {correlationData.samples.length > 0 && (
                            <div>
                                <h4 className="text-md font-medium text-gray-300 mb-2">Sample Details</h4>
                                <div className="max-h-[300px] overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 bg-gray-800">
                                            <tr className="text-gray-400 border-b border-gray-700">
                                                <th className="text-left py-2 px-3">Entity</th>
                                                <th className="text-left py-2 px-3">Isolation</th>
                                                <th className="text-left py-2 px-3">Rank</th>
                                                <th className="text-left py-2 px-3">Query</th>
                                                <th className="text-left py-2 px-3">Note</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {correlationData.samples.map((sample, idx) => (
                                                <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                                                    <td className="py-2 px-3 text-gray-300 font-mono text-xs">
                                                        {sample.entity_id.substring(0, 8)}...
                                                    </td>
                                                    <td className="py-2 px-3 text-white">
                                                        {sample.isolation.toFixed(4)}
                                                    </td>
                                                    <td className={`py-2 px-3 font-medium ${
                                                        sample.rank !== null
                                                            ? sample.rank <= 5 
                                                                ? 'text-green-400' 
                                                                : sample.rank <= 20 
                                                                    ? 'text-yellow-400' 
                                                                    : 'text-red-400'
                                                            : 'text-gray-500'
                                                    }`}>
                                                        {sample.rank !== null ? sample.rank : '-'}
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-400 max-w-xs truncate">
                                                        {sample.query?.substring(0, 40) || '-'}
                                                        {sample.query && sample.query.length > 40 ? '...' : ''}
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-500 text-xs">
                                                        {sample.note || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!correlationData && !loadingCorrelation && !correlationError && (
                    <div className="text-center py-8 text-gray-500">
                        Click "Run Correlation Analysis" to analyze the relationship between entity isolation (uniqueness in embedding space) and search rank.
                    </div>
                )}
            </div>

            {/* Entity Neighbours Display Sections */}
            {neighboursData && (
                <>
                    {/* Closest Neighbours Section */}
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4">
                            Closest Neighbours
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                            Showing the single closest neighbour for each entity using {distanceMetric} distance metric.
                        </p>
                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {Object.entries(neighboursData.neighbours).map(([entityId, neighbour]) => (
                                <EntityNeighboursItem 
                                    key={entityId} 
                                    entityId={entityId} 
                                    neighbour={neighbour}
                                    entityContent={neighboursData.entity_contents[entityId]}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Most Isolated Entities Section */}
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4">
                            Most Isolated Entities (Top {topIsolated})
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                            Entities that are furthest from their closest neighbour using {distanceMetric} distance metric.
                        </p>
                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {neighboursData.most_isolated.map((entity, idx) => {
                                const contentStr = entity.content && typeof entity.content === 'object'
                                    ? JSON.stringify(entity.content)
                                    : String(entity.content || '');
                                const truncatedContent = contentStr.length > 300 
                                    ? contentStr.substring(0, 300) + '...' 
                                    : contentStr;
                                
                                return (
                                    <div key={entity.entity_id} className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-sm font-bold text-red-400">
                                                        #{idx + 1}
                                                    </span>
                                                    <span className="text-sm font-medium text-white">
                                                        {entity.entity_id.substring(0, 8)}...
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        (Distance to closest: {entity.distance_to_closest.toFixed(4)})
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-300 break-words">
                                                    {truncatedContent}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default DimensionAnalytics;

